import { SelectOption } from "../components/react/primitives/Select/Select";

class UserMediaService {
  private static audioInputStream: MediaStreamTrack | null = null;
  private static videoInputStream: MediaStreamTrack | null = null;
  private static _audioInputDeviceId: string = 'default';
  private static _videoInputDeviceId: string = 'default';
  public static isAudioInputEnabled: boolean = false;
  public static isVideoInputEnabled: boolean = false;

  private static isGetUserMediaAudioInProgress = false;
  private static isGetUserMediaVideoInProgress = false;
  private static getUserMediaAudioRequestQueue: { resolve: (value: MediaStreamTrack) => void; reject: (reason?: any) => void; }[] = [];
  private static getUserMediaVideoRequestQueue: { resolve: (value: MediaStreamTrack) => void; reject: (reason?: any) => void; }[] = [];

  private static _audioContext: AudioContext | undefined = undefined;

  public static getAudioContext(): AudioContext {
    if (!this._audioContext) {
      this._audioContext = new AudioContext();
    }
    return this._audioContext;
  }

  public static async getUserMediaAudio(): Promise<MediaStreamTrack> {
    if (this.isTrackLive(this.audioInputStream) && this.audioInputStream) {
      return this.audioInputStream;
    }

    if (this.isGetUserMediaAudioInProgress) {
      return new Promise((resolve, reject) => {
        this.getUserMediaAudioRequestQueue.push({ resolve, reject });
      });
    }

    this.isGetUserMediaAudioInProgress = true;

    try {
      const constraints: MediaStreamConstraints = { audio: { deviceId: { exact: this._audioInputDeviceId } } };
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      this.audioInputStream = mediaStream.getTracks()[0];
      this.isAudioInputEnabled = true;

      this.getUserMediaAudioRequestQueue.forEach(({ resolve, reject }) => {
        const streamState = this.audioInputStream === null ? 'null' : this.audioInputStream.readyState;
        this.isTrackLive(this.audioInputStream) && this.audioInputStream !== null ? resolve(this.audioInputStream) : reject(`stream is ${streamState}`);

      });
    } catch (error) {
      this.getUserMediaAudioRequestQueue.forEach(({ reject }) => reject(error));
      throw error;
    } finally {
      this.isGetUserMediaAudioInProgress = false;
      this.getUserMediaAudioRequestQueue = [];
    }

    return this.audioInputStream;
  }

  public static async getUserMediaVideo(): Promise<MediaStreamTrack> {
    if (this.isTrackLive(this.videoInputStream) && this.videoInputStream) {
      return this.videoInputStream;
    }

    if (this.isGetUserMediaVideoInProgress) {
      return new Promise((resolve, reject) => {
        this.getUserMediaVideoRequestQueue.push({ resolve, reject });
      });
    }

    this.isGetUserMediaVideoInProgress = true;

    try {
      const constraints: MediaStreamConstraints = { video: { deviceId: { exact: this._videoInputDeviceId } } };
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      this.videoInputStream = mediaStream.getTracks()[0];
      this.isVideoInputEnabled = true;

      this.getUserMediaVideoRequestQueue.forEach(({ resolve, reject }) => {
        const streamState = this.videoInputStream === null ? 'null' : this.videoInputStream.readyState;
        this.isTrackLive(this.videoInputStream) && this.videoInputStream !== null ? resolve(this.videoInputStream) : reject(`stream is ${streamState}`);
      });
    } catch (error) {
      this.getUserMediaVideoRequestQueue.forEach(({ reject }) => reject(error));
      throw error;
    } finally {
      this.isGetUserMediaVideoInProgress = false;
      this.getUserMediaVideoRequestQueue = [];
    }

    return this.videoInputStream;
  }


  static get audioInputDeviceId() {
    return this._audioInputDeviceId;
  }

  static get videoInputDeviceId() {
    return this._videoInputDeviceId;
  }

  public static async shouldEnableMic(enable: boolean, isConnectedToRoom: boolean) {
    if (isConnectedToRoom) {
      this.isAudioInputEnabled = enable;
      return this.isAudioInputEnabled;
    } else {
      if (!enable) this.stopAudioTrack();
      else if (enable && !this.isTrackLive(this.audioInputStream)) {
        try {
          const stream = await this.getUserMediaAudio();
          // if (this.audioInputTrack === null || this.audioInputTrack.readyState === 'ended') throw new Error();
        } catch(e) {
          return this.isAudioInputEnabled;
        }
      }
      this.isAudioInputEnabled = enable;
      return this.isAudioInputEnabled;
    }
  }

  public static async shouldEnableCamera(enable: boolean) {
    if (!enable) this.stopVideoTrack();
    else if (enable && !this.isTrackLive(this.videoInputStream)) {
      try {
        const stream = await this.getUserMediaVideo();
        // if (this.videoInputTrack === null || this.videoInputTrack.readyState === 'ended') throw new Error();
      } catch(e) {
        return this.isVideoInputEnabled;
      }
    }
    this.isVideoInputEnabled = enable;
    return this.isVideoInputEnabled;
  }

  public static async toggleMic(isConnectedToRoom: boolean) {
    return this.shouldEnableMic(!this.isAudioInputEnabled, isConnectedToRoom);
  }

  public static async toggleCam() {
    return this.shouldEnableCamera(!this.isVideoInputEnabled);
  }

  private static isTrackLive(track: MediaStreamTrack | null) {
    if (!track) return false;
    return track.readyState === 'live';
  }

  // this needs to be fixed later! (if device is already started, start new one after switch)
  public static updateAudioInputDevice(deviceId: string) {
    this.stopAudioTrack();
    this._audioInputDeviceId = deviceId;
  }

  public static updateVideoInputDevice(deviceId: string) {
    this.stopVideoTrack();
    this._videoInputDeviceId = deviceId;
  }

  public static stopMediaTracks() {
    this.stopAudioTrack();
    this.stopVideoTrack();
  }

  public static stopAudioTrack() {
    const track = this.audioInputStream;
    if (track && track.readyState === 'live') track.stop();
  }

  public static stopVideoTrack() {
    const track = this.videoInputStream;
    if (track && track.readyState === 'live') track.stop();
  }

  private constructor() {}

  public static getDeviceOptions = (devices: MediaDeviceInfo[]) => {
    const options: SelectOption[] = [];
    for (const device of devices) {
      options.push({
        key: device.deviceId,
        value: device.deviceId,
        displayValue: device.label,
        option: device
      });
    }
    return options;
  }

  public static async getInputDevices() {
    try {
      const mediaDevices = await navigator.mediaDevices.enumerateDevices();
  
      const audioInputDevices = mediaDevices.filter(device => device.kind === 'audioinput');
      const videoInputDevices = mediaDevices.filter(device => device.kind === 'videoinput');

      return { audioInputDevices, videoInputDevices };
    } catch (error) {
      console.error('Error accessing media devices:', error);
      throw error;
    }
  };

  public static async getMicPermissions(): Promise<PermissionState> {
    const micPermissions = await navigator.permissions.query({ name: ('microphone' as PermissionName) });
    return micPermissions.state;
  }

  public static async getCameraPermissions(): Promise<PermissionState> {
    const cameraPermissions = await navigator.permissions.query({ name: ('camera' as PermissionName) });
    return cameraPermissions.state;
  }

  public static async requestPermissions(mic: boolean, camera: boolean) {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: mic, video: camera });
    stream.getTracks().forEach(track => track.stop());
  } 
}

export default UserMediaService;
